import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_BUILDER_BUILD_TAB_PATH, CRM_CLIENTS_PAGE } from 'src/constants';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import useNavigate from 'src/hooks/useNavigate';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  AppBuilder,
  useListAppBuildersQuery,
} from 'src/services/api/appBuilderApi';
import {
  useGetOnboardingTaskStatusQuery,
  useUpdateStudioTaskStatusMutation,
} from 'src/services/api/improvedOnboardingApi';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

import {
  EXPLORE_PORTAL_TASK_ID,
  getStudioChecklistCompletionPct,
  getStudioChecklistTaskStatuses,
  GUIDED_TOUR_FIRST_CLIENT,
  GUIDED_TOUR_PARAM,
  STUDIO_CHECKLIST_TASK_CODE_BY_ID,
  STUDIO_CHECKLIST_TASK_IDS,
  StudioChecklistTask,
  StudioChecklistTaskId,
  StudioChecklistTaskStatus,
} from './studioChecklist';

// Fallback destinations. `publishApp` falls back to the new-build page only when
// the user has no draft to resume (see getMostRecentDraftAppBuilder below).
const TASK_ROUTES: Record<StudioChecklistTaskId, string> = {
  publishApp: APP_BUILDER_BUILD_TAB_PATH,
  // Carry the guided-tour intent so the CRM page starts the coachmark tour on arrival.
  explorePortal: `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CLIENT}`,
};

const APP_BUILDER_SESSION_PATH_PREFIX = '/app-setup/build/session/';

// A "draft" app has been created but never published (published apps carry a
// deploymentUrl) and isn't in a terminal failed/stopped state — i.e. something
// the user can still take to publishing.
const isDraftAppBuilder = (app: AppBuilder): boolean =>
  !app.deploymentUrl && app.status !== 'failed' && app.status !== 'stopped';

// The most recently created draft, so "publish your first app" resumes the app
// the user just built instead of dropping them on a blank new build. createdAt
// is an ISO-8601 string, so lexical comparison orders it chronologically.
const getMostRecentDraftAppBuilder = (
  apps: AppBuilder[] | undefined,
): AppBuilder | undefined => {
  const drafts = (apps ?? []).filter(isDraftAppBuilder);
  if (drafts.length === 0) return undefined;
  // Seedless reduce: the first draft seeds the accumulator, so `latest` is
  // always defined and the comparison keeps the newest createdAt.
  return drafts.reduce((latest, app) =>
    app.createdAt > latest.createdAt ? app : latest,
  );
};

export interface UseStudioChecklistResult {
  tasks: StudioChecklistTask[];
  /** Whole-number completion percentage (0-100) for the progress bar. */
  completionPct: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /** Navigates to the task's destination route. */
  onTaskClick: (taskId: StudioChecklistTaskId) => void;
  /** Navigates to a route and closes the primary sidebar on mobile. */
  navigateAndCloseMobileSidebar: (path: string) => void;
  shouldRender: boolean;
  /**
   * True once every studio task is done. Drives the "You're all set" title while
   * the card stays mounted for the rest of the session (it hides on next load).
   */
  allTasksCompleted: boolean;
  /**
   * True when onboarding was already complete when the sidebar loaded — the state
   * that hides the checklist. The upgrade CTA takes the checklist's place, so it
   * keys off this snapshot rather than `allTasksCompleted`: completing the last
   * task mid-session keeps the checklist showing "You're all set" and defers the
   * CTA to the next load, so the two surfaces never appear at once.
   */
  completedOnLoad: boolean;
}

export const useStudioChecklist = (): UseStudioChecklistResult => {
  const { navigate } = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { EnableAppBuilder } = useFeatureFlags();
  // The workspace kind straight off portal config, not `useIsStudioUser` — the branch
  // below must key off Classic vs Studio alone, never off the FTUX flag's state.
  const { isStudioUser } = useContext(PortalConfigContext);
  const { data: onboardingStatus, isSuccess } = useGetOnboardingTaskStatusQuery(
    {},
  );
  const [updateStudioTaskStatus] = useUpdateStudioTaskStatusMutation();
  const { data: builderApps } = useListAppBuildersQuery(undefined, {
    skip: !EnableAppBuilder,
  });
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  // Collapse is ephemeral UI state — not worth persisting.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  const navigateAndCloseMobileSidebar = useCallback(
    (path: string) => {
      navigate(path);
      // Match sidebar nav items: close the primary sidebar on mobile after navigating.
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    },
    [navigate, dispatch, isPrimarySidebarOpenOnMobile],
  );

  const taskStatuses = useMemo(
    () =>
      getStudioChecklistTaskStatuses(onboardingStatus?.additionalFields.tasks),
    [onboardingStatus],
  );

  // Classic workspaces only: they get no test client and no CRM coachmark tour, so the
  // guided "sign in as your client" flow that completes this task server-side can never
  // run. The task is still open only when the workspace has never had a client — the
  // backend marks it done the moment the first one is created — so there is no client
  // experience to preview: tick it off in place instead of routing to an empty CRM.
  const completeExploreWithoutClient =
    !isStudioUser &&
    taskStatuses[EXPLORE_PORTAL_TASK_ID] !== StudioChecklistTaskStatus.Done;

  const onTaskClick = useCallback(
    (taskId: StudioChecklistTaskId) => {
      if (taskId === EXPLORE_PORTAL_TASK_ID && completeExploreWithoutClient) {
        void updateStudioTaskStatus({
          taskCode: STUDIO_CHECKLIST_TASK_CODE_BY_ID[EXPLORE_PORTAL_TASK_ID],
          status: StudioChecklistTaskStatus.Done,
        });
        return;
      }
      let route = TASK_ROUTES[taskId];
      if (taskId === 'publishApp') {
        const draft = getMostRecentDraftAppBuilder(builderApps);
        if (draft) {
          route = `${APP_BUILDER_SESSION_PATH_PREFIX}${draft.id}`;
        }
      }
      navigateAndCloseMobileSidebar(route);
    },
    [
      navigateAndCloseMobileSidebar,
      builderApps,
      completeExploreWithoutClient,
      updateStudioTaskStatus,
    ],
  );

  const tasks: StudioChecklistTask[] = useMemo(() => {
    const taskLabels: Record<StudioChecklistTaskId, string> = {
      publishApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.publish_app,
      ),
      explorePortal: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.explore_portal,
      ),
    };
    return STUDIO_CHECKLIST_TASK_IDS.map((id) => ({
      id,
      label: taskLabels[id],
      status: taskStatuses[id],
    }));
  }, [t, taskStatuses]);

  const completionPct = getStudioChecklistCompletionPct(taskStatuses);

  const studioTasks = onboardingStatus?.additionalFields.tasks ?? [];
  const hasTasks = studioTasks.length > 0;
  const allTasksCompleted = hasTasks && studioTasks.every((task) => task.done);

  const completedOnLoadRef = useRef<boolean | null>(null);
  // Snapshot the "already completed" state only once real task data has arrived.
  // A success response that momentarily lacks tasks would otherwise lock the ref
  // to a stale `false`, so a later refetch delivering completed tasks could never
  // hide the checklist for a done user.
  if (completedOnLoadRef.current === null && isSuccess && hasTasks) {
    completedOnLoadRef.current = allTasksCompleted;
  }
  const hiddenOnLoad = completedOnLoadRef.current === true;

  return {
    tasks,
    completionPct,
    isCollapsed,
    toggleCollapse,
    onTaskClick,
    navigateAndCloseMobileSidebar,
    shouldRender: isSuccess && !hiddenOnLoad,
    allTasksCompleted,
    completedOnLoad: hiddenOnLoad,
  };
};
